import { WarningIcon } from "@chakra-ui/icons";
import { Box, Flex, Text } from "@chakra-ui/react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { Button, Card } from "components";
import { Component } from "react";

const ERROR_MESSAGE = "Something went wrong while loading this data.";

type DisplayPerformanceErrorBoundaryProps = {
  children: React.ReactNode;
  cardTitle?: string;
  queryClient?: QueryClient;
};

const withQueryClient =
  (Component: React.ComponentType<DisplayPerformanceErrorBoundaryProps>) =>
  (props: Omit<DisplayPerformanceErrorBoundaryProps, "queryClient">) => {
    const queryClient = useQueryClient();
    return <Component queryClient={queryClient} {...props} />;
  };

const ErrorContent = ({ onRetry }: { onRetry: () => void }) => (
  <Flex
    alignItems="center"
    justifyContent="center"
    w="100%"
    h="100%"
    minH="150px"
    p={4}
  >
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      maxW="250px"
      gap={2}
    >
      <WarningIcon height="24px" width="24px" color="red.400" />
      <Text fontWeight={500} fontSize="xs" textAlign="center">
        {ERROR_MESSAGE}
      </Text>
      <Box>
        <Button size="xs" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      </Box>
    </Flex>
  </Flex>
);

class DisplayPerformanceErrorBoundaryClass extends Component<
  DisplayPerformanceErrorBoundaryProps,
  { hasError: boolean }
> {
  constructor(props: DisplayPerformanceErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  handleRetry = () => {
    this.props.queryClient?.clear();
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.cardTitle) {
        return (
          <Box mb={4} w="100%">
            <Card title={this.props.cardTitle}>
              <ErrorContent onRetry={this.handleRetry} />
            </Card>
          </Box>
        );
      }

      return <ErrorContent onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}

const DisplayPerformanceErrorBoundary = withQueryClient(
  DisplayPerformanceErrorBoundaryClass
);

export { DisplayPerformanceErrorBoundary };
